import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getChartTitle } from '@/shared/copy/charts';
import { s } from '../../theme/tokens';
import { lightModeChartStatusColors, uiTokens } from '../../theme/uiTokens';
import { formatDuration } from '../formatters/formatDuration';
import { Box } from './Box';
import { formatChartDateLabel, formatChartTooltipDateLabel } from './chartDateLabel';
import { resolveChartPlotAreaBackgroundColor } from './chartPlotAreaBackgroundColor';
import { buildChartPlotAreaFillSx, buildChartPlotAreaFrameSegmentSx } from './chartPlotAreaLayers';
import { ChartSwatch } from './ChartSwatch';
import { buildAdaptiveTickLabelInterval } from './chartTickLabelInterval';
import { getChartTooltipSx } from './chartTooltipStyles';
import { useTheme } from './hooks';
import { LineChartTooltip } from './LineChartTooltip';
import { Text } from './Text';
import {
  buildTooltipOverlaySurfaceSx,
  tooltipOverlayBodyFontWeight,
  tooltipOverlayCompactLineHeight,
  tooltipOverlayFontSize,
  tooltipOverlayLabelFontWeight,
  tooltipOverlayMetricFontWeight,
  tooltipOverlayPaddingX,
  tooltipOverlayPaddingY,
  tooltipOverlayTextColor,
  tooltipOverlayTitleFontSize,
} from './tooltipOverlayStyles';
import { useLineChartPlotBounds } from './useLineChartPlotBounds';
import { useRecentChartItemSelection } from './useRecentChartItemSelection';
import { MuiLineChart } from './xCharts';
import type { ChartsAxisData } from '@mui/x-charts/models';
import type { LineItemIdentifier } from '@mui/x-charts/models/seriesType';
import type { MouseEvent as ReactMouseEvent } from 'react';

export type LineChartDatum = Record<string, string | number | null>;

type LineSeries = {
  key: string;
  label: string;
  color?: string;
};

const metricNumberFormatter = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 2,
});

const resolveSeriesLabel = (item: LineSeries): string => {
  const trimmed = item.label.trim();
  if (trimmed.length > 0) return trimmed;
  if (item.key.toLowerCase() === 'passrate') return getChartTitle('passRate');
  if (item.key.toLowerCase() === 'flakescore') return getChartTitle('flakeScore');
  if (item.key.toLowerCase() === 'frequency') return 'Executions';
  if (item.key.toLowerCase() === 'duration') return getChartTitle('avgDuration');
  return '';
};

type LineChartProps = {
  data: LineChartDatum[];
  series: LineSeries[];
  backgroundColor?: string;
  height?: number;
  marginLeft?: number;
  marginRight?: number;
  marginTop?: number;
  marginBottom?: number;
  xAxisHeight?: number;
  yAxisWidth?: number;
  yAxisTickNumber?: number;
  yAxisTickInterval?: number[];
  axisStrokeWidth?: number;
  showLegend?: boolean;
  showHorizontalGrid?: boolean;
  showPlotAreaBackground?: boolean;
  showPlotAreaFrame?: boolean;
  hideXAxisLine?: boolean;
  hideYAxisLine?: boolean;
  hideXAxisLabels?: boolean;
  hideYAxisLabels?: boolean;
  hideXAxisTicks?: boolean;
  hideYAxisTicks?: boolean;
  showAllXAxisLabels?: boolean;
  xAxisLabelFormatter?: (label: string) => string;
  tooltipLabelFormatter?: (label: string) => string;
  onSelectBar?: (dataIndex: number) => void;
  showPoints?: boolean;
  filledPoints?: boolean;
  disableInteraction?: boolean;
  disableTooltip?: boolean;
  tooltipEmptyValueLabel?: string;
  hideTooltipSeriesLabel?: boolean;
  tooltipValueFormatter?: (params: {
    seriesKey: string;
    value: number | null;
    dataIndex: number | null;
  }) => string;
  yDomainPaddingRatio?: number;
  yDomainPaddingTopRatio?: number;
  yDomainPaddingBottomRatio?: number;
  yAxisMin?: number;
  yAxisMax?: number;
  yReferenceLines?: Array<{ value: number; color: string; label?: string }>;
};

export const LineChart = ({
  data,
  series,
  backgroundColor,
  height = 260,
  marginLeft = -8,
  marginRight = 16,
  marginTop = 16,
  marginBottom = 14,
  xAxisHeight: xAxisHeightOverride,
  yAxisWidth = 72,
  yAxisTickNumber,
  yAxisTickInterval,
  axisStrokeWidth = 1,
  showLegend = true,
  showHorizontalGrid = true,
  showPlotAreaBackground = true,
  showPlotAreaFrame = false,
  hideXAxisLine = true,
  hideYAxisLine = false,
  hideXAxisLabels = false,
  hideYAxisLabels = false,
  hideXAxisTicks = false,
  hideYAxisTicks = false,
  showAllXAxisLabels = false,
  xAxisLabelFormatter = formatChartDateLabel,
  tooltipLabelFormatter = formatChartTooltipDateLabel,
  onSelectBar,
  showPoints = false,
  filledPoints = false,
  disableInteraction = false,
  disableTooltip = false,
  tooltipEmptyValueLabel = '',
  hideTooltipSeriesLabel = false,
  tooltipValueFormatter,
  yDomainPaddingRatio = 0,
  yDomainPaddingTopRatio,
  yDomainPaddingBottomRatio,
  yAxisMin: yAxisMinOverride,
  yAxisMax: yAxisMaxOverride,
  yReferenceLines = [],
}: LineChartProps): JSX.Element => {
  const theme = useTheme();
  const statusColors =
    theme.palette.mode === 'light' ? lightModeChartStatusColors : uiTokens.statusColors;
  const lineChartPalette = useMemo(
    () => [
      statusColors.purple,
      statusColors.green,
      statusColors.orange,
      statusColors.blue,
      statusColors.red,
    ],
    [statusColors],
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const hasLegendLabels = series.some((item) => item.label.trim().length > 0);
  const hideLegend = !showLegend || !hasLegendLabels;
  const xLabels = useMemo(() => data.map((item) => String(item.label)), [data]);
  const tooltipLabels = useMemo(
    () => data.map((item) => tooltipLabelFormatter(String(item.label))),
    [data, tooltipLabelFormatter],
  );
  const estimatedXAxisWidth =
    typeof containerWidth === 'number'
      ? Math.max(0, containerWidth - yAxisWidth - marginRight - Math.abs(marginLeft))
      : null;
  const xTickLabelInterval = buildAdaptiveTickLabelInterval(
    xLabels.length,
    showAllXAxisLabels,
    estimatedXAxisWidth,
  );
  const xAxisHeight = xAxisHeightOverride ?? (hideXAxisLabels && hideXAxisTicks ? 16 : 24);
  const plotAreaBackgroundColor = resolveChartPlotAreaBackgroundColor(theme);
  const plotAreaInset = Math.max(0, axisStrokeWidth);
  const { markRecentItemSelection, shouldSuppressAxisSelection } = useRecentChartItemSelection();
  const handleItemClick = useCallback(
    (_event: ReactMouseEvent<SVGElement>, item: LineItemIdentifier) => {
      if (!onSelectBar) return;
      const dataIndex = item.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      markRecentItemSelection(dataIndex);
      onSelectBar(dataIndex);
    },
    [markRecentItemSelection, onSelectBar],
  );
  const handleAxisClick = useCallback(
    (_event: MouseEvent, axisData: ChartsAxisData | null) => {
      if (!onSelectBar) return;
      const dataIndex = axisData?.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      if (shouldSuppressAxisSelection(dataIndex)) {
        return;
      }
      onSelectBar(dataIndex);
    },
    [onSelectBar, shouldSuppressAxisSelection],
  );
  const formatTooltipValue = useCallback(
    (seriesKey: string, value: number | null): string => {
      if (typeof value !== 'number' || !Number.isFinite(value)) return tooltipEmptyValueLabel;
      const normalizedSeriesKey = seriesKey.toLowerCase();
      if (normalizedSeriesKey === 'duration') {
        return formatDuration(value * 1000);
      }
      if (
        normalizedSeriesKey === 'passrate' ||
        normalizedSeriesKey === 'flakescore' ||
        normalizedSeriesKey === 'value'
      ) {
        return `${metricNumberFormatter.format(value)}%`;
      }
      return metricNumberFormatter.format(value);
    },
    [tooltipEmptyValueLabel],
  );
  const isDurationChart = series.some((item) => item.key.toLowerCase() === 'duration');
  const isPercentageChart = series.some((item) =>
    ['flakescore', 'passrate', 'value'].includes(item.key.toLowerCase()),
  );
  const seriesMin = series.reduce((minValue, item) => {
    const itemMin = data.reduce((rowMin, row) => {
      const rawValue = row[item.key];
      const value = typeof rawValue === 'number' ? rawValue : null;
      return value !== null && Number.isFinite(value) && value < rowMin ? value : rowMin;
    }, Number.POSITIVE_INFINITY);
    return itemMin < minValue ? itemMin : minValue;
  }, Number.POSITIVE_INFINITY);
  const seriesMax = series.reduce((maxValue, item) => {
    const itemMax = data.reduce((rowMax, row) => {
      const rawValue = row[item.key];
      const value = typeof rawValue === 'number' ? rawValue : null;
      return value !== null && Number.isFinite(value) && value > rowMax ? value : rowMax;
    }, 0);
    return itemMax > maxValue ? itemMax : maxValue;
  }, 0);
  const formatYAxisValue = (value: number | null): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '';
    if (!isDurationChart) return String(value);
    return formatDuration(value * 1000);
  };
  const finiteReferenceLines = yReferenceLines.filter((line) => Number.isFinite(line.value));
  const referenceMax = finiteReferenceLines.reduce(
    (maxValue, line) => (line.value > maxValue ? line.value : maxValue),
    0,
  );
  const rawYAxisMin = Number.isFinite(seriesMin) ? Math.min(0, seriesMin) : 0;
  const rawYAxisMax = isPercentageChart ? 100 : Math.max(seriesMax, referenceMax);
  const yAxisRange = rawYAxisMax - rawYAxisMin;
  const yAxisPaddingBase =
    yAxisRange > 0 ? yAxisRange : rawYAxisMax !== 0 ? Math.abs(rawYAxisMax) : 1;
  const resolvedTopPaddingRatio = yDomainPaddingTopRatio ?? yDomainPaddingRatio;
  const resolvedBottomPaddingRatio = yDomainPaddingBottomRatio ?? yDomainPaddingRatio;
  const yAxisTopPadding =
    resolvedTopPaddingRatio > 0 ? yAxisPaddingBase * resolvedTopPaddingRatio : 0;
  const yAxisBottomPadding =
    resolvedBottomPaddingRatio > 0 ? yAxisPaddingBase * resolvedBottomPaddingRatio : 0;
  const yAxisMin =
    typeof yAxisMinOverride === 'number'
      ? yAxisMinOverride
      : isPercentageChart
        ? 0
        : rawYAxisMin - yAxisBottomPadding;
  const yAxisMax =
    typeof yAxisMaxOverride === 'number'
      ? yAxisMaxOverride
      : isPercentageChart
        ? 100
        : rawYAxisMax + yAxisTopPadding;
  const yAxisTickTranslateX = isDurationChart ? -8 : -6;
  const tooltipTrigger = disableTooltip ? 'none' : undefined;
  const plotBounds = useLineChartPlotBounds({
    containerRef,
    data,
    height,
    marginLeft,
    marginTop,
    series,
    yAxisWidth,
  });
  const tooltipSx = {
    ...(getChartTooltipSx(theme) as object),
    '& .flowqa-line-tooltip-paper': {
      ...buildTooltipOverlaySurfaceSx(theme),
      maxWidth: 'min(calc(100vw - 32px), 360px)',
      width: 'max-content',
      paddingTop: `${tooltipOverlayPaddingY} !important`,
      paddingBottom: `${tooltipOverlayPaddingY} !important`,
      paddingLeft: '0 !important',
      paddingRight: '0 !important',
    },
    '& .flowqa-line-tooltip-table': {
      borderCollapse: 'separate',
      borderSpacing: 0,
      width: '100% !important',
      tableLayout: 'auto',
    },
    '& .flowqa-line-tooltip-table > thead > tr > th': {
      borderBottom: '0 !important',
      boxShadow: 'none !important',
      paddingLeft: `${tooltipOverlayPaddingX} !important`,
      paddingRight: `${tooltipOverlayPaddingX} !important`,
      paddingTop: '0 !important',
      paddingBottom: `${tooltipOverlayPaddingY} !important`,
      textAlign: 'left !important',
      verticalAlign: 'middle !important',
      color: `${tooltipOverlayTextColor} !important`,
      fontSize: `${tooltipOverlayTitleFontSize} !important`,
      fontWeight: `${tooltipOverlayLabelFontWeight} !important`,
      lineHeight: `${String(tooltipOverlayCompactLineHeight)} !important`,
    },
    '& .flowqa-line-tooltip-divider-row': {
      height: '1px !important',
    },
    '& .flowqa-line-tooltip-divider-cell': {
      backgroundColor: 'transparent !important',
      backgroundImage: `linear-gradient(${theme.app.colors.border}, ${theme.app.colors.border}) !important`,
      backgroundPosition: 'center top !important',
      backgroundRepeat: 'no-repeat !important',
      backgroundSize: `calc(100% - (${tooltipOverlayPaddingX} * 2)) 1px !important`,
      borderBottom: '0 !important',
      borderTop: '0 !important',
      fontSize: '0 !important',
      height: '0 !important',
      lineHeight: '0 !important',
      padding: '0 !important',
    },
    '& .flowqa-line-tooltip-table > tbody > tr > th, & .flowqa-line-tooltip-table > tbody > tr > td':
      {
        paddingTop: '2px !important',
        paddingBottom: '2px !important',
        paddingLeft: `${tooltipOverlayPaddingX} !important`,
        paddingRight: `${tooltipOverlayPaddingX} !important`,
        verticalAlign: 'middle !important',
        lineHeight: `${String(tooltipOverlayCompactLineHeight)} !important`,
      },
    '& .flowqa-line-tooltip-table > tbody > tr > th': {
      textAlign: 'left !important',
      color: `${tooltipOverlayTextColor} !important`,
      whiteSpace: 'nowrap',
    },
    '& .flowqa-line-tooltip-table > tbody > tr > td': {
      textAlign: 'right !important',
      whiteSpace: 'nowrap',
      fontVariantNumeric: 'tabular-nums',
      fontWeight: `${tooltipOverlayMetricFontWeight} !important`,
    },
    '& .flowqa-line-tooltip-table > tbody > tr:first-of-type > th, & .flowqa-line-tooltip-table > tbody > tr:first-of-type > td':
      {
        paddingTop: '8px !important',
      },
    '& .flowqa-line-tooltip-table > tbody > tr:last-of-type > th, & .flowqa-line-tooltip-table > tbody > tr:last-of-type > td':
      {
        paddingBottom: '4px !important',
      },
    '& .flowqa-line-tooltip-cell, & .flowqa-line-tooltip-table th, & .flowqa-line-tooltip-table td':
      {
        color: tooltipOverlayTextColor,
        fontSize: tooltipOverlayFontSize,
        lineHeight: tooltipOverlayCompactLineHeight,
        fontWeight: tooltipOverlayBodyFontWeight,
      },
    '& .flowqa-line-tooltip-table tr': {
      lineHeight: `${String(tooltipOverlayCompactLineHeight)} !important`,
    },
  };

  const tooltipLabelsByAxisValue = useMemo(() => {
    const labelsByAxisValue = new Map<string, string>();
    xLabels.forEach((label, index) => {
      const tooltipLabel = tooltipLabels[index] ?? label;
      labelsByAxisValue.set(label, tooltipLabel);
      labelsByAxisValue.set(xAxisLabelFormatter(label), tooltipLabel);
    });
    return labelsByAxisValue;
  }, [tooltipLabels, xAxisLabelFormatter, xLabels]);

  const chartSeries = useMemo(
    () =>
      series.map((item, index) => ({
        data: data.map((row) => {
          const value = row[item.key];
          return typeof value === 'number' && Number.isFinite(value) ? value : null;
        }),
        label: resolveSeriesLabel(item),
        color: item.color ?? lineChartPalette[index % lineChartPalette.length],
        showMark: showPoints,
        valueFormatter: (value: number | null, context: { dataIndex?: number }) =>
          tooltipValueFormatter
            ? tooltipValueFormatter({
                seriesKey: item.key,
                value,
                dataIndex:
                  typeof context.dataIndex === 'number' && Number.isFinite(context.dataIndex)
                    ? context.dataIndex
                    : null,
              })
            : formatTooltipValue(item.key, value),
      })),
    [data, formatTooltipValue, lineChartPalette, series, showPoints, tooltipValueFormatter],
  );
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const updateWidth = (): void => {
      const nextWidth = container.clientWidth;
      setContainerWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
    };
    updateWidth();
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });
    resizeObserver.observe(container);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        position: 'relative',
        overflow: 'visible',
        backgroundColor: backgroundColor ?? theme.app.colors.surface,
        color: theme.app.colors.divider,
        '& .MuiChartsSurface-root': {
          color: theme.app.colors.divider,
          backgroundColor: 'transparent !important',
        },
        '& .MuiChartsSurface-root svg': {
          backgroundColor: 'transparent !important',
        },
        '& .MuiChartsSurface-root rect': {
          fill: 'transparent !important',
        },
        '& .MuiChartsLegend-root': { display: 'none' },
        '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
          stroke: theme.app.colors.divider,
          strokeOpacity: 1,
          strokeWidth: axisStrokeWidth,
        },
        '& .MuiChartsAxis-root .MuiChartsAxis-line, & .MuiChartsAxis-root .MuiChartsAxis-tick': {
          stroke: theme.app.colors.divider,
          strokeOpacity: 1,
          strokeWidth: axisStrokeWidth,
        },
      }}
    >
      {showPlotAreaBackground && plotBounds ? (
        <Box
          sx={buildChartPlotAreaFillSx({
            backgroundColor: plotAreaBackgroundColor,
            inset: plotAreaInset,
            plotBounds,
          })}
        />
      ) : null}
      {showPlotAreaFrame && plotBounds
        ? (['top', 'left', 'right', 'bottom'] as const).map((side) => (
            <Box
              key={`plot-frame-${side}`}
              sx={buildChartPlotAreaFrameSegmentSx({
                color: theme.app.colors.divider,
                inset: 0,
                plotBounds,
                side,
                strokeWidth: axisStrokeWidth,
              })}
            />
          ))
        : null}
      <MuiLineChart
        height={height}
        slots={{
          tooltip: (tooltipProps) => (
            <LineChartTooltip
              {...tooltipProps}
              formatTooltipValue={formatTooltipValue}
              hideTooltipSeriesLabel={hideTooltipSeriesLabel}
              tooltipLabelsByAxisValue={tooltipLabelsByAxisValue}
            />
          ),
        }}
        onLineClick={onSelectBar && !disableInteraction ? handleItemClick : undefined}
        onAxisClick={onSelectBar && !disableInteraction ? handleAxisClick : undefined}
        series={chartSeries}
        xAxis={[
          {
            data: xLabels,
            scaleType: 'point',
            height: xAxisHeight,
            valueFormatter: (value) => xAxisLabelFormatter(String(value)),
            tickLabelInterval: hideXAxisLabels ? () => false : xTickLabelInterval,
            tickLabelStyle: {
              fill: hideXAxisLabels ? 'transparent' : theme.app.colors.textMuted,
              fontSize: hideXAxisLabels ? '0px' : '13px',
              transform: hideXAxisLabels ? 'none' : 'translateY(4px)',
            },
            tickSize: hideXAxisTicks ? 0 : 6,
          },
        ]}
        yAxis={[
          {
            min: yAxisMin,
            max: yAxisMax > 0 ? yAxisMax : undefined,
            width: yAxisWidth,
            ...(typeof yAxisTickNumber === 'number' ? { tickNumber: yAxisTickNumber } : {}),
            ...(Array.isArray(yAxisTickInterval) && yAxisTickInterval.length > 0
              ? { tickInterval: yAxisTickInterval }
              : {}),
            tickLabelStyle: {
              fill: hideYAxisLabels ? 'transparent' : theme.app.colors.textMuted,
              fontSize: hideYAxisLabels ? '0px' : '13px',
            },
            tickSize: hideYAxisTicks ? 0 : 0,
            valueFormatter: formatYAxisValue,
          },
        ]}
        grid={{ horizontal: showHorizontalGrid, vertical: false }}
        margin={{ left: marginLeft, right: marginRight, top: marginTop, bottom: marginBottom }}
        slotProps={{ tooltip: { sx: tooltipSx, trigger: tooltipTrigger } }}
        sx={{
          position: 'relative',
          zIndex: 1,
          ...(!showPlotAreaBackground
            ? {
                backgroundColor: 'transparent !important',
                '& .MuiChartsSurface-root': {
                  color: 'transparent !important',
                  fill: 'transparent !important',
                  backgroundColor: 'transparent !important',
                },
                '& .MuiChartsSurface-root *': {
                  backgroundColor: 'transparent !important',
                },
                '& .MuiChartsSurface-root rect': {
                  fill: 'transparent !important',
                },
                '& svg': {
                  backgroundColor: 'transparent !important',
                },
              }
            : null),
          '& .MuiChartsAxis-tickLabel': { fill: theme.app.colors.textMuted },
          '& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
            transform: `translateX(${yAxisTickTranslateX}px)`,
          },
          '& .MuiChartsAxis-root': { opacity: 1 },
          '& .MuiChartsGrid-line': {
            stroke: showHorizontalGrid ? theme.app.colors.divider : 'transparent',
          },
          '& .MuiChartsAxis-line[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
            strokeWidth: `${axisStrokeWidth}px !important`,
          },
          '& .MuiChartsAxis-tick[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
            strokeWidth: `${axisStrokeWidth}px !important`,
          },
          '& .MuiChartsAxis-directionX .MuiChartsAxis-line, & .MuiChartsAxis-directionX .MuiChartsAxis-line[stroke]':
            hideXAxisLine || showPlotAreaFrame
              ? {
                  stroke: 'transparent !important',
                }
              : {
                  stroke: `${theme.app.colors.divider} !important`,
                  strokeWidth: `${axisStrokeWidth}px !important`,
                },
          '& .MuiChartsAxis-directionY .MuiChartsAxis-line, & .MuiChartsAxis-directionY .MuiChartsAxis-line[stroke]':
            hideYAxisLine || showPlotAreaFrame
              ? {
                  stroke: 'transparent !important',
                }
              : {
                  stroke: `${theme.app.colors.divider} !important`,
                  strokeWidth: `${axisStrokeWidth}px !important`,
                },
          '& .MuiChartsAxisHighlight-root, & .MuiChartsAxisHighlight': {
            display: 'none !important',
          },
          '& .MuiChartsAxisHighlight-rect, & .MuiChartsAxisHighlightRect': {
            fill: 'transparent !important',
            opacity: 0,
            display: 'none !important',
          },
          '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlightLine': {
            stroke: 'transparent !important',
            display: 'none',
          },
          '& .MuiChartsLegend-root': { display: 'none' },
          ...(filledPoints
            ? {
                '& .MuiMarkElement-root': {
                  fill: 'currentColor',
                  strokeWidth: 2,
                },
              }
            : null),
          ...(onSelectBar && !disableInteraction
            ? {
                '& .MuiChartsAxis-directionX': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tickLabel': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tick': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlight-rect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsAxisHighlightLine, & .MuiChartsAxisHighlightRect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsOverlay-root': { cursor: 'pointer' },
                '& svg': { cursor: 'pointer' },
              }
            : null),
          ...(disableInteraction
            ? {
                '& .MuiChartsOverlay-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight': { pointerEvents: 'none' },
                '& .MuiLineElement-root': { pointerEvents: 'none' },
                '& .MuiMarkElement-root': { pointerEvents: 'none' },
              }
            : null),
        }}
      >
        {finiteReferenceLines.map((line, index) => (
          <ChartsReferenceLine
            key={`line-guide-${line.value}-${line.color}-${index}`}
            y={line.value}
            lineStyle={{ stroke: line.color, strokeDasharray: '8 6', strokeWidth: 0.5 }}
          />
        ))}
      </MuiLineChart>
      {!hideLegend ? (
        <Box sx={{ display: 'grid', gap: s('xs'), marginTop: s('sm') }}>
          {series.map((item, index) => (
            <Box key={item.key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: s('xs') }}>
                <ChartSwatch
                  color={item.color ?? lineChartPalette[index % lineChartPalette.length]}
                />
                <Text role="body" sx={{ fontSize: '13px' }}>
                  {item.label}
                </Text>
              </Box>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};
